import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { W, H, FPS, bayer, makePainter } from "./paint";
import { SCENES } from "./scenes";

const FONT_CSS = "https://cdn.jsdelivr.net/npm/galmuri@2.40.3/dist/galmuri.css";
const SWAP_MS = 650; // 장면 전환 (도트가 흩어지듯)

/** 픽셀 폰트(갈무리, OFL)를 한 번만 불러옴 */
export const usePixelFont = (on = true) => {
  useEffect(() => {
    if (!on || document.querySelector(`link[href="${FONT_CSS}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT_CSS;
    document.head.appendChild(link);
  }, [on]);
};

const reduceMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * 픽셀 장면 화면. scene 이 바뀌면 도트 디더로 전환하고,
 * cues([{ name, at }]) 는 그 장면 안의 연출 시작 시각입니다.
 */
export const PixelStage = ({ scene, cues, hidden = false }) => {
  const ref = useRef(null);
  const state = useRef({ scene: null, start: 0, prev: null, prevStart: 0, swapAt: -1e9 });
  const cuesRef = useRef(cues);
  cuesRef.current = cues;

  useEffect(() => {
    const s = state.current;
    if (scene === s.scene) return;
    const now = performance.now();
    s.prev = s.scene;
    s.prevStart = s.start;
    s.scene = scene;
    s.start = now;
    s.swapAt = s.prev ? now : -1e9;
  }, [scene]);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const off = [0, 1].map(() => {
      const c = document.createElement("canvas");
      c.width = W;
      c.height = H;
      return c;
    });
    const still = reduceMotion();
    let raf = 0;
    let last = -1;

    const drawScene = (target, key, startedAt, now, live) => {
      const fn = SCENES[key] || SCENES.blank;
      const g = makePainter(target);
      const t = still ? 3 : Math.floor(((now - startedAt) / 1000) * FPS) / FPS;
      const cue = (name) => {
        if (!live) return -1;
        let at = -1;
        cuesRef.current.forEach((c) => {
          if (c.name === name) at = c.at; // 장면이 바뀌면 플레이어가 cues 를 비움
        });
        if (at < 0) return -1;
        return still ? 9 : Math.floor(((now - at) / 1000) * FPS) / FPS;
      };
      fn(g, t, cue);
    };

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const f = Math.floor(now / (1000 / FPS));
      if (f === last) return;
      last = f;
      const s = state.current;
      const k = still ? 1 : (now - s.swapAt) / SWAP_MS;
      if (k >= 1 || !s.prev) {
        drawScene(ctx, s.scene, s.start, now, true);
        return;
      }
      // 전환: 이전 장면과 새 장면을 베이어 순서로 섞음
      const [a, b] = off.map((c) => c.getContext("2d"));
      drawScene(a, s.prev, s.prevStart, now, false);
      drawScene(b, s.scene, s.start, now, true);
      const da = a.getImageData(0, 0, W, H);
      const db = b.getImageData(0, 0, W, H);
      const step = Math.floor(k * 6) / 6; // 끊기는 전환
      for (let y = 0; y < H; y += 1)
        for (let x = 0; x < W; x += 1) {
          if (bayer(x >> 1, y >> 1) < step) {
            const i = (y * W + x) * 4;
            da.data[i] = db.data[i];
            da.data[i + 1] = db.data[i + 1];
            da.data[i + 2] = db.data[i + 2];
          }
        }
      ctx.putImageData(da, 0, 0);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <Frame style={hidden ? { display: "none" } : undefined}>
      <Canvas ref={ref} width={W} height={H} aria-hidden="true" />
    </Frame>
  );
};

const Frame = styled.div`
  position: relative;
  flex: none;
  margin: auto 14px 0; /* 장면 + 대화창을 화면 가운데로 */
  padding: 3px;
  background: #fff4dc;
  box-shadow: 0 0 0 3px #0b0a12, 0 6px 0 3px rgba(0, 0, 0, 0.35);
  /* 픽셀 모서리 */
  clip-path: polygon(0 3px, 3px 3px, 3px 0, calc(100% - 3px) 0, calc(100% - 3px) 3px, 100% 3px,
    100% calc(100% - 3px), calc(100% - 3px) calc(100% - 3px), calc(100% - 3px) 100%, 3px 100%,
    3px calc(100% - 3px), 0 calc(100% - 3px));
`;

const Canvas = styled.canvas`
  display: block;
  width: 100%;
  aspect-ratio: ${W} / ${H};
  image-rendering: pixelated;
  image-rendering: crisp-edges;
  background: #0b0a12;
`;
